/**
 * Brand styles — the CSS half of the animation system.
 *
 * Everything tunable is a custom property on the element (the "knobs"):
 * size, inks, the five space accents, durations, staggers, easings. The
 * keyframes are named per part (`brand-stem-draw`, `brand-bowl-swing`,
 * `brand-dot-drop`, `brand-letter-in`) so a pure-CSS consumer can play the
 * enter choreography by toggling the `brand-play-enter` class — the typed
 * TS API in `animation.ts` drives the same parts via WAAPI instead (for
 * promise-composable timelines) and reads its timing from the SAME knobs.
 *
 * `installBrandStyles(doc)` is idempotent per document: adopted stylesheet
 * where constructable stylesheets exist, `<style data-kit-brand>` fallback
 * otherwise. `defineBrandMark()` calls it — apps rarely need to.
 */

import { SPACE_ACCENTS } from "./spaces";

export const BRAND_STYLES_CSS = /* css */ `
kit-brand-mark {
  /* ---- knobs: geometry + color ---------------------------------------- */
  --brand-size: 3.5rem;               /* mark height; everything scales from it */
  --brand-ink: #f2ede3;               /* cream on charcoal — dark theme first */
  --brand-ink-soft: color-mix(in oklab, var(--brand-ink) 62%, transparent);
  --brand-ink-faint: color-mix(in oklab, var(--brand-ink) 34%, transparent);
  --brand-font: "Fraunces", Georgia, "Times New Roman", serif;

  /* the five spaces (operator-tunable) */
  --brand-space-personal: ${SPACE_ACCENTS.personal};
  --brand-space-business: ${SPACE_ACCENTS.business};
  --brand-space-family: ${SPACE_ACCENTS.family};
  --brand-space-community: ${SPACE_ACCENTS.community};
  --brand-space-public: ${SPACE_ACCENTS.public};
  --brand-accent: var(--brand-ink);   /* no space set → the dot stays ink */

  /* ---- knobs: motion --------------------------------------------------- */
  --brand-enter-duration: 640ms;      /* per-part unit for the enter timeline */
  --brand-switch-duration: 360ms;     /* per-phase unit for app-switch */
  --brand-letter-stagger: 36ms;
  --brand-ease-out: cubic-bezier(0.22, 1, 0.36, 1);
  --brand-ease-swing: cubic-bezier(0.34, 1.42, 0.44, 1);
  --brand-ease-in: cubic-bezier(0.55, 0, 0.7, 0.3);

  display: inline-flex;
  align-items: flex-end;
  gap: calc(var(--brand-size) * 0.22);
  max-width: 100%;
  color: var(--brand-ink);
}

kit-brand-mark[space="personal"]  { --brand-accent: var(--brand-space-personal); }
kit-brand-mark[space="business"]  { --brand-accent: var(--brand-space-business); }
kit-brand-mark[space="family"]    { --brand-accent: var(--brand-space-family); }
kit-brand-mark[space="community"] { --brand-accent: var(--brand-space-community); }
kit-brand-mark[space="public"]    { --brand-accent: var(--brand-space-public); }

kit-brand-mark .brand-mark {
  height: var(--brand-size);
  width: auto;
  overflow: visible;
  display: block;
  flex: 0 0 auto;
}

kit-brand-mark .p-stem { fill: var(--brand-ink); }
kit-brand-mark .p-bowl {
  fill: var(--brand-ink);
  transform-box: view-box;
  transform-origin: 90px 16.5px; /* ANCHORS.bowlHinge — the swing hinge */
}
kit-brand-mark .p-dot {
  fill: var(--brand-accent);
  transform-box: fill-box;
  transform-origin: center;
}
kit-brand-mark .app-name {
  fill: var(--brand-ink);
  font-family: var(--brand-font);
  font-weight: 500;
  font-size: 57px; /* user units — SLOT.fontSize */
  letter-spacing: 0.01em;
}

/* ---- lockup tail: divider + breadcrumb -------------------------------- */
kit-brand-mark .divider {
  flex: 0 0 auto;
  width: 1px;
  align-self: flex-end;
  height: calc(var(--brand-size) * 0.24);
  /* bottom of the bar a touch below the text baseline (at the mark's bottom cut) */
  margin-bottom: calc(var(--brand-size) * 0.06);
  background: var(--brand-ink-faint);
}
kit-brand-mark .breadcrumb {
  display: flex;
  align-items: baseline;
  gap: 0.55em;
  min-width: 0;
  font-family: var(--brand-font);
  font-weight: 400;
  font-size: calc(var(--brand-size) * 0.115); /* trail stays small — the big name is the lockup's voice */
  line-height: 1;
  /* sit the crumb baseline on the mark's baseline (156/172 of height) */
  margin-bottom: calc(var(--brand-size) * 0.093 - 0.22em);
  color: var(--brand-ink-soft);
  white-space: nowrap;
}
kit-brand-mark .crumb {
  color: var(--brand-ink-soft);
  flex: 0 0 auto;
}
kit-brand-mark .crumb:last-of-type {
  color: var(--brand-ink);
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis; /* truncation is part of the design */
}
kit-brand-mark .crumb-sep {
  color: var(--brand-ink-faint);
  flex: 0 0 auto;
  font-size: 0.82em;
}

/* ---- named keyframes, one per part ------------------------------------ */
@keyframes brand-stem-draw {
  from { clip-path: inset(-10% -10% 110% -10%); }
  to   { clip-path: inset(-10% -10% -10% -10%); }
}
@keyframes brand-bowl-swing {
  0%   { transform: rotate(-16deg); opacity: 0; }
  45%  { opacity: 1; }
  70%  { transform: rotate(2.6deg); }
  100% { transform: rotate(0deg); opacity: 1; }
}
@keyframes brand-dot-drop {
  0%   { transform: translateY(-38px); opacity: 0; }
  55%  { transform: translateY(0);     opacity: 1; }
  72%  { transform: translateY(-10px); }
  86%  { transform: translateY(0); }
  94%  { transform: translateY(-2.5px); }
  100% { transform: translateY(0); }
}
@keyframes brand-letter-in {
  from { fill-opacity: 0; }
  to   { fill-opacity: 1; }
}

/* Pure-CSS enter choreography: add .brand-play-enter to the element. The
   typed API (element.play("enter")) uses WAAPI on the same parts instead. */
kit-brand-mark.brand-play-enter .p-stem {
  animation: brand-stem-draw var(--brand-enter-duration) var(--brand-ease-out) both;
}
kit-brand-mark.brand-play-enter .p-bowl {
  animation: brand-bowl-swing calc(var(--brand-enter-duration) * 1.1)
    var(--brand-ease-swing) calc(var(--brand-enter-duration) * 0.45) both;
}
kit-brand-mark.brand-play-enter .app-letter {
  animation: brand-letter-in calc(var(--brand-enter-duration) * 0.5)
    var(--brand-ease-out) calc(var(--brand-enter-duration) * 0.7 + var(--i, 0) * var(--brand-letter-stagger)) both;
}
kit-brand-mark.brand-play-enter .p-dot {
  animation: brand-dot-drop calc(var(--brand-enter-duration) * 1.05)
    var(--brand-ease-out) calc(var(--brand-enter-duration) * 1.15) both;
}

@media (prefers-reduced-motion: reduce) {
  kit-brand-mark.brand-play-enter .p-stem,
  kit-brand-mark.brand-play-enter .p-bowl,
  kit-brand-mark.brand-play-enter .app-letter,
  kit-brand-mark.brand-play-enter .p-dot {
    animation: none;
  }
}
`;

const INSTALLED = new WeakSet<Document>();

/** Install the brand stylesheet once per document (idempotent). */
export function installBrandStyles(doc: Document = document): void {
  if (INSTALLED.has(doc)) return;
  INSTALLED.add(doc);
  const CtorOk =
    typeof CSSStyleSheet !== "undefined" &&
    "adoptedStyleSheets" in doc &&
    typeof CSSStyleSheet.prototype.replaceSync === "function";
  if (CtorOk) {
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(BRAND_STYLES_CSS);
      doc.adoptedStyleSheets = [...doc.adoptedStyleSheets, sheet];
      return;
    } catch {
      /* fall through to the <style> tag */
    }
  }
  if (doc.querySelector("style[data-kit-brand]")) return;
  const style = doc.createElement("style");
  style.setAttribute("data-kit-brand", "");
  style.textContent = BRAND_STYLES_CSS;
  doc.head.append(style);
}
